import React, { useState } from "react"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterSegment, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }

const compareModes = [
	{ id: "year_dow", label: "Год назад (тот же день недели)" },
	{ id: "year", label: "Год назад (те же даты)" },
	{ id: "period", label: "Пред. период" },
]

const medalColors = ["#f0b429", "#9aa5b1", "#cd7f32"]

// Рост от нулевой базы бэкенд отдаёт null: это «новое» (если сейчас что-то есть) или «—»
function formatGrowth(value, current) {
	if (value === null || value === undefined) return Number(current || 0) > 0 ? "новое" : "—"
	const number = Number(value)
	return `${number >= 0 ? "+" : ""}${number.toFixed(1).replace(/\.0$/, "")}%`
}

function GrowthBadge({ value, current }) {
	if (value === null || value === undefined) {
		if (Number(current || 0) > 0) {
			return (
				<span style={{ background: "var(--dark-tint-primary, #eef2ff)", color: "var(--dark-fg-primary, #4c6ef0)", borderRadius: "12px", padding: "3px 10px", fontWeight: 700, fontSize: "12px" }}>
					новое
				</span>
			)
		}
		return <span className="text-muted">—</span>
	}
	const positive = Number(value) >= 0
	return (
		<span
			style={{
				background: positive ? "var(--dark-tint-success, #e7f8ef)" : "var(--dark-tint-danger, #fdecec)",
				color: positive ? "var(--dark-fg-success, #1f9d57)" : "#d23434",
				borderRadius: "12px",
				padding: "3px 10px",
				fontWeight: 600,
				fontSize: "12px",
				whiteSpace: "nowrap",
			}}
		>
			{positive ? "↗" : "↘"} {formatGrowth(value, current)}
		</span>
	)
}

function KpiCard({ title, hint, value, unit, metric = {}, compareUnit, compareDigits = 0 }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body p-4">
				<div style={mutedStyle}>{title}</div>
				<div className="d-flex align-items-baseline gap-2 mt-1">
					<h2 className="mb-0" style={{ fontSize: "34px", fontWeight: 800, color: "var(--dark-text-strong, #111827)", letterSpacing: "-0.5px" }}>
						{value}
					</h2>
					<span style={mutedStyle}>{unit}</span>
				</div>
				<div className="d-flex flex-wrap align-items-center gap-2 mt-2" style={{ fontSize: "13px" }}>
					<GrowthBadge value={metric.growth_percent} current={metric.current} />
					<span className="text-muted">vs {formatMoney(metric.compare, compareDigits)} {compareUnit}</span>
				</div>
				{hint && <div className="mt-2" style={mutedStyle}>{hint}</div>}
			</div>
		</div>
	)
}

function BranchTable({ branches }) {
	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-1">Сравнение по филиалам</h5>
				<div className="mb-3" style={mutedStyle}>Только сопоставимые филиалы — с выручкой в обоих периодах</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Филиал</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Текущий</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>База</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Изменение выручки</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Чеки</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Изменение чеков</th>
							</tr>
						</thead>
						<tbody>
							{branches.length === 0 && (
								<tr><td colSpan="6" className="text-muted text-center py-4">Нет сопоставимых филиалов за выбранный период</td></tr>
							)}
							{branches.map((branch, index) => {
								const borderBottom = index === branches.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								return (
									<tr key={branch.pos_id}>
										<td className="py-3" style={{ borderBottom }}>
											<div className="d-flex align-items-center gap-2">
												<span
													className="d-flex align-items-center justify-content-center fw-bold"
													style={{
														width: "26px",
														height: "26px",
														fontSize: "12px",
														borderRadius: "50%",
														color: index < 3 ? "#fff" : "var(--dark-text-muted, #8791a1)",
														background: index < 3 ? medalColors[index] : "var(--dark-surface-2, #f1f4f8)",
													}}
												>
													{index + 1}
												</span>
												<span style={{ fontWeight: 600 }}>{branch.pos_name}</span>
											</div>
										</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(branch.current_revenue, 0)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(branch.compare_revenue, 0)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>
											<GrowthBadge value={branch.revenue_growth_percent} current={branch.current_revenue} />
										</td>
										<td className="py-3 text-end" style={{ borderBottom }}>
											{formatMoney(branch.current_cheques, 0)}
											<div style={mutedStyle}>база {formatMoney(branch.compare_cheques, 0)}</div>
										</td>
										<td className="py-3 text-end" style={{ borderBottom }}>
											<GrowthBadge value={branch.cheques_growth_percent} current={branch.current_cheques} />
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

// Новые / закрытые филиалы: в LFL не входят, показываем отдельно для полноты картины
function ExcludedBranchTable({ title, hint, branches }) {
	if (!branches.length) return null
	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-1">{title}</h5>
				<div className="mb-3" style={mutedStyle}>{hint}</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Филиал</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Выручка сейчас</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Выручка в базе</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Чеки сейчас</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Чеки в базе</th>
							</tr>
						</thead>
						<tbody>
							{branches.map((branch, index) => {
								const borderBottom = index === branches.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								return (
									<tr key={branch.pos_id}>
										<td className="py-3" style={{ borderBottom, fontWeight: 600 }}>{branch.pos_name}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(branch.current_revenue, 0)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(branch.compare_revenue, 0)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(branch.current_cheques, 0)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(branch.compare_cheques, 0)}</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function Lfl() {
	const [data, setData] = useState(null)

	const filters = useAnalyticsFilters({
		...buildPeriod("7days"),
		pos_id: -1,
		order_type_ids: [],
		source_ids: [],
		compare_mode: "year_dow",
	}, searchWithFilter)
	const { draft, applied } = filters
	const options = useAnalyticsOptions(["sources"], draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/lfl", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	function exportExcel() {
		if (!data) return
		const growthCell = (value, current) => formatGrowth(value, current)
		const branchRows = (list, group) => list
			.map(branch => `<tr><td>${group}</td><td>${branch.pos_name}</td><td>${branch.current_revenue}</td><td>${branch.compare_revenue}</td><td>${growthCell(branch.revenue_growth_percent, branch.current_revenue)}</td><td>${branch.current_cheques}</td><td>${branch.compare_cheques}</td><td>${growthCell(branch.cheques_growth_percent, branch.current_cheques)}</td></tr>`)
			.join("")
		const rows = branchRows(data.branches || [], "LFL")
			+ branchRows(data.new_branches || [], "Новый")
			+ branchRows(data.closed_branches || [], "Закрыт / без продаж")
		const html = `<table border="1"><thead><tr><th>Группа</th><th>Филиал</th><th>Выручка текущая</th><th>Выручка база</th><th>Изм. выручки</th><th>Чеки текущие</th><th>Чеки база</th><th>Изм. чеков</th></tr></thead><tbody>${rows}</tbody></table>`
		const blob = new Blob([`﻿${html}`], { type: "application/vnd.ms-excel" })
		const link = document.createElement("a")
		link.href = URL.createObjectURL(blob)
		link.download = `lfl-${data.start_date}_${data.end_date}.xls`
		link.click()
		URL.revokeObjectURL(link.href)
	}

	const summary = data?.summary || {}
	const summaryAll = data?.summary_all || {}
	const comparableHint = data ? `Сопоставимые филиалы: ${formatMoney(data.comparable_count, 0)} из ${formatMoney(data.branch_count, 0)}` : ""

	return (
		<>
			<AnalyticsFilterBar
				title="LFL"
				icon="uil-chart-line"
				subtitle={data?.subtitle || "Like-for-like: сравнение сопоставимых филиалов"}
				filters={filters}
				actions={
					<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "10px", fontWeight: 600 }} onClick={exportExcel}>
						<i className="uil uil-file-export me-1"></i>
						Excel
					</button>
				}
			>
				<StandardFields filters={filters} options={options} />
			</AnalyticsFilterBar>

			<div className="card mb-3" style={cardStyle}>
				<div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
					<div className="d-flex flex-wrap align-items-center gap-4">
						<div>
							<div style={mutedStyle}>Текущий период</div>
							<b>{data?.current_period_label || "—"}</b>
						</div>
						<span className="text-muted fw-bold">VS</span>
						<div>
							<div style={mutedStyle}>Период сравнения</div>
							<b>{data?.compare_period_label || "—"}</b>
						</div>
						<span style={{ ...mutedStyle, background: "var(--dark-surface-2, #f1f4f8)", borderRadius: "12px", padding: "4px 12px", fontWeight: 600, color: "var(--dark-text-strong, #1f2937)" }}>
							{data?.period_days || 0} дн.
						</span>
					</div>
					<FilterSegment
						options={compareModes}
						value={applied.compare_mode}
						onChange={(id) => filters.patchApplied({ compare_mode: id })}
					/>
				</div>
			</div>

			<div className="row g-3 mb-2">
				<div className="col-xl-4 col-md-6">
					<KpiCard
						title="Выручка LFL, за вычетом возвратов"
						hint={comparableHint}
						value={formatMoney(summary.revenue?.current, 0)}
						unit="сум"
						metric={summary.revenue}
						compareUnit="сум"
					/>
				</div>
				<div className="col-xl-4 col-md-6">
					<KpiCard
						title="Чеки LFL"
						hint={comparableHint}
						value={formatMoney(summary.cheques?.current, 0)}
						unit="чеков"
						metric={summary.cheques}
						compareUnit="чеков"
					/>
				</div>
				<div className="col-xl-4 col-md-6">
					<KpiCard
						title="Средний чек LFL"
						hint={comparableHint}
						value={formatMoney(summary.average_cheque?.current, 0)}
						unit="сум"
						metric={summary.average_cheque}
						compareUnit="сум"
					/>
				</div>
			</div>

			{data && (
				<div className="mb-3 px-1" style={mutedStyle}>
					Вся сеть (включая новые и закрытые): выручка {formatMoney(summaryAll.revenue?.current, 0)} сум
					({formatGrowth(summaryAll.revenue?.growth_percent, summaryAll.revenue?.current)}),
					чеки {formatMoney(summaryAll.cheques?.current, 0)}
					({formatGrowth(summaryAll.cheques?.growth_percent, summaryAll.cheques?.current)}),
					средний чек {formatMoney(summaryAll.average_cheque?.current, 0)} сум
					({formatGrowth(summaryAll.average_cheque?.growth_percent, summaryAll.average_cheque?.current)})
				</div>
			)}

			<BranchTable branches={data?.branches || []} />

			<ExcludedBranchTable
				title="Новые филиалы — не входят в LFL"
				hint="Нет выручки в периоде сравнения: открылись позже"
				branches={data?.new_branches || []}
			/>
			<ExcludedBranchTable
				title="Закрытые / без продаж в текущем периоде — не входят в LFL"
				hint="Была выручка в периоде сравнения, сейчас продаж нет"
				branches={data?.closed_branches || []}
			/>
		</>
	)
}

export default Lfl
